/**
 * @file hooks/useTextToSpeech.ts
 * 7.3-08 — Lecture à voix haute des réponses agent (Web Speech API,
 * speechSynthesis). 100% front, dégradation gracieuse si non supporté.
 *
 * Réglages utilisateur (activer/désactiver + vitesse) partagés entre toutes
 * les instances du hook via un mini-store module (useSyncExternalStore) et
 * persistés dans localStorage.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

// ── Réglages persistés (partagés entre instances) ────────────────────────────

export interface TtsSettings {
  /** Lecture à voix haute activée (bouton "Écouter" visible). */
  enabled: boolean;
  /** Vitesse de lecture (rate SpeechSynthesisUtterance). */
  rate: number;
}

const SETTINGS_KEY = 'assistia.chat.tts.v1';
/** Cycle de vitesses proposé par le bouton "vitesse". */
export const TTS_RATES: readonly number[] = [0.75, 1, 1.25, 1.5];

function loadSettings(): TtsSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        const p = parsed as Partial<TtsSettings>;
        return {
          enabled: typeof p.enabled === 'boolean' ? p.enabled : true,
          rate: typeof p.rate === 'number' && p.rate > 0 ? p.rate : 1,
        };
      }
    }
  } catch {
    /* stockage indisponible → défauts */
  }
  return { enabled: true, rate: 1 };
}

let settingsSnapshot: TtsSettings = loadSettings();
const listeners = new Set<() => void>();

function setSettings(patch: Partial<TtsSettings>): void {
  settingsSnapshot = { ...settingsSnapshot, ...patch };
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settingsSnapshot));
  } catch {
    /* quota / indisponible : le réglage reste en mémoire */
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): TtsSettings {
  return settingsSnapshot;
}

// ── Nettoyage du Markdown avant synthèse ─────────────────────────────────────

/**
 * Retire la syntaxe Markdown pour ne pas lire les symboles à voix haute
 * (astérisques, backticks, crochets de liens…).
 */
export function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ') // blocs de code (non lus)
    .replace(/`([^`]*)`/g, '$1') // code inline
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // images → alt
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // liens → libellé
    .replace(/^#{1,6}\s+/gm, '') // titres
    .replace(/(\*\*|__)(.*?)\1/g, '$2') // gras
    .replace(/(\*|_)(.*?)\1/g, '$2') // italique
    .replace(/^\s*[-*+]\s+/gm, '') // puces
    .replace(/^\s*\d+\.\s+/gm, '') // listes numérotées
    .replace(/^\s*>\s?/gm, '') // citations
    .replace(/<[^>]+>/g, ' ') // HTML résiduel
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface TextToSpeech {
  /** speechSynthesis disponible sur ce navigateur ? */
  supported: boolean;
  /** Une lecture lancée par CETTE instance est-elle en cours ? */
  speaking: boolean;
  /** Lecture en pause ? */
  paused: boolean;
  /** Voix disponibles (asynchrone selon les navigateurs). */
  voices: SpeechSynthesisVoice[];
  /** Réglage persisté : lecture activée. */
  enabled: boolean;
  /** Réglage persisté : vitesse. */
  rate: number;
  setEnabled: (v: boolean) => void;
  /** Passe à la vitesse suivante du cycle TTS_RATES et la persiste. */
  cycleRate: () => void;
  /** Lit un texte (le Markdown est nettoyé avant synthèse). */
  speak: (text: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

export function useTextToSpeech(lang = 'fr-FR'): TextToSpeech {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const settings = useSyncExternalStore(subscribe, getSnapshot);

  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Chargement des voix (peut être asynchrone : événement voiceschanged).
  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    const refresh = () => setVoices(synth.getVoices());
    refresh();
    synth.addEventListener('voiceschanged', refresh);
    return () => synth.removeEventListener('voiceschanged', refresh);
  }, [supported]);

  // Au démontage : on coupe la lecture si cette instance l'a lancée.
  useEffect(
    () => () => {
      if (supported && utterRef.current) {
        window.speechSynthesis.cancel();
      }
    },
    [supported],
  );

  const stop = useCallback(() => {
    if (!supported) return;
    utterRef.current = null;
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  }, [supported]);

  const speak = useCallback(
    (text: string) => {
      if (!supported) return;
      const synth = window.speechSynthesis;
      synth.cancel(); // une seule lecture à la fois dans l'app

      const utter = new SpeechSynthesisUtterance(stripMarkdown(text));
      utter.lang = lang;
      utter.rate = getSnapshot().rate;
      // Voix fr-FR sélectionnée automatiquement, repli voix par défaut.
      const frVoice =
        synth.getVoices().find((v) => v.lang === lang) ??
        synth.getVoices().find((v) => v.lang.startsWith(lang.slice(0, 2)));
      if (frVoice) utter.voice = frVoice;

      const onDone = () => {
        utterRef.current = null;
        setSpeaking(false);
        setPaused(false);
      };
      utter.onend = onDone;
      utter.onerror = onDone;

      utterRef.current = utter;
      setSpeaking(true);
      setPaused(false);
      synth.speak(utter);
    },
    [supported, lang],
  );

  const pause = useCallback(() => {
    if (!supported || !speaking) return;
    window.speechSynthesis.pause();
    setPaused(true);
  }, [supported, speaking]);

  const resume = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setPaused(false);
  }, [supported]);

  const setEnabled = useCallback((v: boolean) => {
    setSettings({ enabled: v });
  }, []);

  const cycleRate = useCallback(() => {
    const current = getSnapshot().rate;
    const idx = TTS_RATES.indexOf(current);
    const next = TTS_RATES[(idx + 1) % TTS_RATES.length] ?? 1;
    setSettings({ rate: next });
  }, []);

  return {
    supported,
    speaking,
    paused,
    voices,
    enabled: settings.enabled,
    rate: settings.rate,
    setEnabled,
    cycleRate,
    speak,
    pause,
    resume,
    stop,
  };
}
